import { Node, nodePasteRule } from "@tiptap/core";

/**
 * @example https://www.tiktok.com/@lestreetfoodist/video/7335494598585879841
 */
const TIKTOK_REGEX = /^https?:\/\/(www\.)?tiktok\.com\/@[\w.-]+\/video\/(\d+)/;

const TIKTOK_REGEX_GLOBAL = /^https?:\/\/(www\.)?tiktok\.com\/@[\w.-]+\/video\/(\d+)/g;

export function isValidTikTokUrl(url: string) {
    return url.match(TIKTOK_REGEX);
}

export async function getOembedFromTikTokUrl(url: string): Promise<Oembed> {
    return fetch(`https://www.tiktok.com/oembed?url=${url}`).then((response) => {
        if (!response.ok) {
            throw Error(response.statusText);
        }
        return response.json();
    });
}

export interface TikTokOptions {
    /**
     * Controls if the paste handler for tiktok videos should be added.
     * @default true
     * @example false
     */
    addPasteHandler: boolean;

    /**
     * The HTML attributes for a tiktok video node.
     * @default {}
     * @example { class: 'foo' }
     */
    HTMLAttributes: Record<string, any>;

    /**
     * Controls if the tiktok node should be inline or not.
     * @default false
     * @example true
     */
    inline: boolean;
}

type SetTikTokVideoOptions = {
    url: string;
    blockquote?: string;
};

interface Oembed {
    version: string; // e.g., "1.0"
    type: string; // e.g., "video"
    title: string; // Video title
    author_url: string; // URL to the author profile
    author_name: string; // Author display name
    width: string; // Width of the embed, e.g., "100%"
    height: string; // Height of the embed, e.g., "100%"
    html: string; // HTML embed code
    thumbnail_width: number; // Thumbnail width in pixels
    thumbnail_height: number; // Thumbnail height in pixels
    thumbnail_url: string; // URL to thumbnail image
    provider_url: string; // Provider homepage URL
    provider_name: string; // Provider name, e.g., "TikTok"
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        tiktok: {
            /**
             * Insert a tiktok video
             * @param options The tiktok video attributes
             * @example editor.commands.setTikTokVideo({ url: 'https://www.tiktok.com/@lestreetfoodist/video/7335494598585879841' })
             */
            setTikTokVideo: (options: SetTikTokVideoOptions) => ReturnType;
        };
    }
}

export default Node.create<TikTokOptions>({
    name: "tiktok",
    draggable: true,
    atom: true,
    selectable: true,

    addOptions() {
        return {
            addPasteHandler: true,
            HTMLAttributes: {
                class: "not-prose",
            },
            inline: false,
        };
    },

    inline() {
        return this.options.inline;
    },

    group() {
        return this.options.inline ? "inline" : "block";
    },

    addAttributes() {
        return {
            url: {
                default: null,
            },
            blockquote: {
                parseHTML: (element) => {
                    return element.innerHTML;
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-tiktok="video"]',
            },
            {
                tag: "div[data-tiktok-video]",
            },
        ];
    },

    addCommands() {
        return {
            setTikTokVideo:
                (options: SetTikTokVideoOptions) =>
                ({ commands, editor }) => {
                    if (!isValidTikTokUrl(options.url)) {
                        return false;
                    }

                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    });
                },
        };
    },

    addPasteRules() {
        if (!this.options.addPasteHandler) {
            return [];
        }

        return [
            nodePasteRule({
                find: TIKTOK_REGEX_GLOBAL,
                type: this.type,
                getAttributes: (match) => {
                    return { url: match.input };
                },
            }),
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const div = document.createElement("div");

        div.setAttribute("data-tiktok", "video");
        div.setAttribute("url", HTMLAttributes.url);

        if (node.attrs.blockquote) {
            div.innerHTML = node.attrs.blockquote;
        } else {
            div.innerHTML = `TikTok: ${node.attrs.url}`;
        }

        Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
            div.setAttribute(key, value);
        });

        return div;
    },
});
